import React, { Suspense } from 'react';
import { ChartData } from '../types';

interface SafeEChartsRendererProps {
  chartData: ChartData;
  width: number;
  height: number;
  isDarkMode?: boolean;
  theme?: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

// Lazy load the Async ECharts renderer to avoid initialization issues
const EChartsRenderer = React.lazy(() => 
  import('./AsyncEChartsRenderer').then(module => ({
    default: module.AsyncEChartsRenderer
  })).catch(error => {
    console.error('Failed to load AsyncEChartsRenderer:', error);
    // Return a fallback component
    return {
      default: ({ width, height }: SafeEChartsRendererProps) => (
        <div 
          style={{ width: `${width}px`, height: `${height}px` }}
          className="flex items-center justify-center text-gray-500 border border-gray-200 rounded bg-gray-50"
        >
          Chart unavailable
        </div>
      )
    };
  })
);

const ChartLoadingFallback: React.FC<{ width: number; height: number }> = ({ width, height }) => (
  <div 
    style={{ width: `${width}px`, height: `${height}px` }}
    className="flex items-center justify-center text-gray-400 border border-gray-200 rounded bg-gray-50"
  >
    <div className="text-center">
      <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full mx-auto mb-2"></div>
      <div className="text-sm">Loading chart...</div>
    </div>
  </div>
);

const ChartErrorFallback: React.FC<{ width: number; height: number }> = ({ width, height }) => (
  <div 
    style={{ width: `${width}px`, height: `${height}px` }}
    className="flex items-center justify-center text-red-500 border border-red-200 rounded bg-red-50"
  >
    <div className="text-center">
      <div className="text-lg mb-1">⚠️</div>
      <div className="text-sm">Chart error</div>
    </div>
  </div>
);

class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ComponentType<Record<string, unknown>> },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ComponentType<Record<string, unknown>> }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Chart Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback;
      return <FallbackComponent />;
    }

    return this.props.children;
  }
}

export const SafeEChartsRenderer: React.FC<SafeEChartsRendererProps> = (props) => {
  const { width, height } = props;

  return (
    <ChartErrorBoundary fallback={() => <ChartErrorFallback width={width} height={height} />}>
      <Suspense fallback={<ChartLoadingFallback width={width} height={height} />}>
        <EChartsRenderer {...props} />
      </Suspense>
    </ChartErrorBoundary>
  );
};